import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, Card, CardContent } from '@mui/material';
import { useDataProvider, useNotify } from 'react-admin';
import LivePreviewFrame from '../../components/live-preview/LivePreviewFrame';
import CodeEditor from '../../components/code/CodeEditor';

interface SourceBundle {
  files: Record<string, string>;
  dependencies: Record<string, string>;
  config: {
    framework: string;
    language: string;
    entryPoint: string;
    buildCommand: string;
  };
}

const LivePreviewTest: React.FC = () => {
  const [sourceBundle, setSourceBundle] = useState<SourceBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('src/App.tsx');
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const loadReactTemplate = async () => {
    setLoading(true);
    try {
      console.log('📡 Loading React template from API...');
      
      // Call the new API endpoint
      const response = await fetch('/api/userexperience/react-template/source-bundle');
      
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      
      const bundle: SourceBundle = await response.json();
      
      console.log('✅ React template loaded:', {
        files: Object.keys(bundle.files).length,
        dependencies: Object.keys(bundle.dependencies).length,
        framework: bundle.config.framework
      });
      
      setSourceBundle(bundle);
      notify('✅ React template loaded successfully!', { type: 'success' });
      
    } catch (error) {
      console.error('Failed to load React template:', error);
      notify(`❌ Failed to load template: ${error.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateSourceFile = (newContent: string) => {
    if (!sourceBundle || !selectedFile) return;

    const updatedBundle = {
      ...sourceBundle,
      files: {
        ...sourceBundle.files,
        [selectedFile]: newContent
      }
    };

    setSourceBundle(updatedBundle);
    console.log(`📝 Updated file: ${selectedFile}`);
  };

  const handleCompilationResult = (result: any) => {
    console.log('🔨 Compilation result:', result);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Live Preview Test - Browser WASM Compilation
      </Typography>
      
      <Typography variant="body1" color="textSecondary" paragraph>
        Test the browser-based WASM compilation with the React template.
        This demonstrates instant preview capabilities without server-side builds.
      </Typography>

      {/* Load Template Button */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            1. Load React Template
          </Typography>
          <Button
            variant="contained"
            onClick={loadReactTemplate}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {loading ? 'Loading Template...' : 'Load React Template'}
          </Button>
          
          {sourceBundle && (
            <Alert severity="info">
              Template loaded: {Object.keys(sourceBundle.files).length} files, 
              {Object.keys(sourceBundle.dependencies).length} dependencies
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Source Code Editor */}
      {sourceBundle && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              2. Edit Source Code (Live)
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Select file to edit:
              </Typography>
              <select
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                style={{ padding: '8px', marginBottom: '16px', width: '300px' }}
              >
                {Object.keys(sourceBundle.files)
                  .filter(file => file.endsWith('.tsx') || file.endsWith('.ts') || file.endsWith('.css'))
                  .map(file => (
                    <option key={file} value={file}>{file}</option>
                  ))
                }
              </select>
            </Box>

            <CodeEditor
              source="selectedFileContent"
              label={`Editing: ${selectedFile}`}
              language={selectedFile.endsWith('.css') ? 'css' : 'typescript'}
              height="300px"
              defaultValue={sourceBundle.files[selectedFile] || ''}
              onChange={updateSourceFile}
            />
          </CardContent>
        </Card>
      )}

      {/* Live Preview */}
      {sourceBundle && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              3. Live Preview (Browser WASM Compilation)
            </Typography>
            
            <LivePreviewFrame 
              sourceBundle={sourceBundle}
              onCompilationResult={handleCompilationResult}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default LivePreviewTest;
